import { z } from 'zod';
import { FileType, ApplicationType, ReportType, FormType, FormDataType, SectionType, FileuploadSingleElementType, DependType, FileuploadElementType, FormElementType, BudgetLinesElementType, ExpenseTableElementType, OptionType, AccountElementType } from './types';
import { isElementVisible, getElementRequiredOverride, type VisibilityContext } from './restrictedVisibility';
import { formatNumber } from './utils';

/**
 * Et element regnes som "begrenset" hvis det har ett eller flere kriterier
 * satt i form-builder. Slike felter behandles alltid som valgfrie i basis-
 * skjemaet — vi gjenopplever `required` via en superRefine som kun trigger
 * når kriteriene er oppfylt (altså når feltet faktisk vises for søkeren).
 */
const isRestricted = (element: FormElementType): boolean => {
    return !!(element as any)?.restrictedVisibility?.criteria?.length;
};

const isFieldRequired = (element: FormElementType): boolean => {
    return !!(element.required || (element as any).rules?.required);
};

/** Effektiv "required" når vi bygger basis-skjemaet — restricted felter er
 *  aldri required i basis, men håndhevingen flytter over til superRefine. */
const isRequiredForBaseSchema = (element: FormElementType): boolean => {
    return isFieldRequired(element) && !isRestricted(element);
};

/**
 * En "helt tom" rad i budgetlines/expensetable — alle celler er tomme. Slike
 * rader skal ikke blokkere navigasjon: de ignoreres i validering (filtreres
 * bort før rad-/min-sjekk), slik at en etterlatt tom rad ikke hindrer søker.
 */
const isRowEmpty = (row: unknown): boolean => {
    if (!row || typeof row !== 'object') return true;
    return Object.values(row as Record<string, unknown>).every((v) => {
        if (v === null || v === undefined) return true;
        if (typeof v === 'string') return v.trim() === '';
        if (Array.isArray(v)) return v.length === 0;
        return false; // tall, File, vedlegg-objekt e.l. teller som innhold
    });
};

/**
 * Preprocess for rad-baserte felt: normaliser verdien til en array (håndter
 * JSON-streng fra lagret kladd) og fjern helt tomme rader før validering.
 */
const stripEmptyRows = (v: unknown): unknown[] => {
    let arr: unknown[];
    if (Array.isArray(v)) arr = v;
    else if (typeof v === 'string') {
        try { const p = JSON.parse(v); arr = Array.isArray(p) ? p : []; } catch { arr = []; }
    } else arr = [];
    return arr.filter((row) => !isRowEmpty(row));
};

export const checkFileType = (file: FileType, element: FileuploadSingleElementType | FileuploadElementType) => {
    if (file?.name) {
        const fileType = file.name.split('.').pop();
        if (element.extensionsFormat.includes('.' + fileType)) {
            return true;
        }
    }

    return false;
};

const generateValidationSchema = (data: ApplicationType | ReportType, context?: VisibilityContext) => {
    const generateFormSchema = (form: FormType) => {
        const hasRadioGroup = form.elements.some((element) => element.type === 'radiogroupdepending');
        // Kun kontonummer-felt MED KAR-sjekk skal blokkere skjemaet. Felt med
        // `skipKarCheck` gjør ingen KAR-forespørsel, så `valid_account` settes
        // aldri og refine-en under ville ellers låst søker ute for godt.
        const hasAccountField = form.elements.some(
            (element) => element.type === 'account' && !(element as AccountElementType).skipKarCheck
        );
        const hasImageField = form.elements.some((element) => element.name === 'filer_bilder_multiple');
        const hasVideoField = form.elements.some((element) => element.name === 'filer_video_multiple');
        const hasdependFieldPercent = form.elements.some((element) => element.rules?.dependFieldPercent);

        const schema = form.elements.reduce<Record<string, z.ZodTypeAny>>((acc, element) => {
            // Tekstblokk er innhold, ikke et felt: ingen verdi å validere eller
            // lagre. Uten dette hoppet faller den i else-grenen nederst og får
            // et z.string()-felt som havner i formData ved innsending.
            if (element.type === 'textblock') return acc;

            let fieldSchema;
            if (element.type === 'dropdowncheck') {
                // dropdowncheck er alltid multi-select uavhengig av legacy `multiple`-
                // flagg — komponenten i FormElement.tsx returnerer alltid string[].
                // Normalize legacy formats: JSON-string or OptionType[] → string[]
                const normalize = (val: unknown): string[] => {
                    if (typeof val === 'string') {
                        try {
                            const parsed = JSON.parse(val);
                            if (Array.isArray(parsed)) {
                                return parsed.map((i: any) => typeof i === 'object' ? i.value : i);
                            }
                        } catch { /* fall through */ }
                        return [];
                    }
                    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
                        return val.map((i: any) => i.value);
                    }
                    return (val as string[]) ?? [];
                };
                const minSel: number | undefined = (element as any).minSelect;
                const maxSel: number | undefined = (element as any).maxSelect;
                let arr: z.ZodTypeAny = z.preprocess(normalize, z.array(z.string()));
                if (isRequiredForBaseSchema(element) || (minSel && minSel > 0)) {
                    const min = minSel ?? 1;
                    arr = arr.refine(
                        (v) => (v as string[]).length >= min,
                        min === 1 ? `${element.label} er påkrevd` : `Velg minst ${min} alternativ${min > 1 ? 'er' : ''}`
                    );
                }
                if (maxSel) {
                    arr = arr.refine(
                        (v) => (v as string[]).length <= maxSel,
                        `Velg maks ${maxSel} alternativ${maxSel > 1 ? 'er' : ''}`
                    );
                }
                fieldSchema = (isRequiredForBaseSchema(element) || (minSel && minSel > 0)) ? arr : arr.optional();
            } else if (element.type === 'fileuploadsingle') {
                fieldSchema = z.any();

                if (isRequiredForBaseSchema(element)) {
                    fieldSchema = fieldSchema.refine((file: FileType) => file !== null, 'Du må laste opp en fil').refine((file: FileType) => checkFileType(file, element), 'Denne filtypen er ikke tillat');
                } else {
                    fieldSchema = fieldSchema.optional();
                }
            } else if (element.type === 'fileupload') {
                fieldSchema = z.array(z.any());

                if (isRequiredForBaseSchema(element)) {
                    fieldSchema = fieldSchema.refine((files: FileType[]) => files.length > 0, 'Du må laste opp minst en fil').refine((files: FileType[]) => files.every((file) => checkFileType(file, element)), 'En eller flere filer har en ugyldig filtype');
                } else {
                    fieldSchema = fieldSchema.optional();
                }
            } else if (element.type === 'ToggleSwitch' || element.type === 'checkbox') {
                // Begge komponentene lagrer verdien som STRENG ("true"/"false").
                // Uten denne grenen faller checkbox ned i den generiske else-grenen
                // og valideres med .nonempty() — der er "false" en utfylt verdi, så
                // et påkrevd felt slipper igjennom så snart søker har huket av én
                // gang, selv om boksen står tom igjen. Normaliser til boolsk først.
                fieldSchema = z.union([z.boolean(), z.string()]).transform((val) => {
                    // Convert "true"/"false" strings to actual booleans
                    if(element.name === 'confirm_invalid') return val;
                    if (val === 'true') return true;
                    if (val === 'false') return false;
                    return val; // Return as is if already boolean
                });

                if (isRequiredForBaseSchema(element)) {
                    fieldSchema = fieldSchema.refine(
                        (value) => value === true,
                        'Dette er påkrevd' // "This is required"
                    );
                } else {
                    fieldSchema = fieldSchema.optional();
                }
            } else if (element.type === 'budgetlines') {
                // Helt tomme rader ignoreres — en etterlatt tom rad skal ikke
                // hindre at søker går videre. Required teller kun utfylte rader.
                fieldSchema = isRequiredForBaseSchema(element)
                    ? z.preprocess(stripEmptyRows, z.array(z.any()).min(1, 'Du må legge til minst én kostnad'))
                    : z.preprocess(stripEmptyRows, z.array(z.any()));
            }
            else if (element.type === 'expensetable') {
                // Bygg rad-skjema dynamisk fra columns. Faller tilbake til det
                // gamle hardkodede oppsettet hvis malen ikke har columns satt.
                const expenseCols = (element.columns && element.columns.length > 0)
                    ? element.columns
                    : [
                        { name: 'beskrivelse', label: 'Beskrivelse', type: 'text' as const },
                        { name: 'belop',       label: 'Beløp',       type: 'number' as const },
                        { name: 'bilag',       label: 'Bilag',       type: 'attachment' as const },
                    ];

                const shape: Record<string, z.ZodTypeAny> = {};
                expenseCols.forEach((col) => {
                    if (col.type === 'attachment') {
                        shape[col.name] = z.any().refine(
                            (val) => val instanceof File || (
                                val && typeof val.name === 'string' &&
                                (typeof val.download_url === 'string' || typeof val.location === 'string')
                            ),
                            { message: `${col.label || 'Bilag'} mangler` }
                        );
                    } else if (col.type === 'number') {
                        shape[col.name] = z.union([z.string().min(1, `${col.label || 'Tall'} mangler`), z.number()]);
                    } else {
                        shape[col.name] = z.string().min(1, `${col.label || 'Felt'} mangler`);
                    }
                });
                const rowSchema = z.object(shape);
                // Helt tomme rader filtreres bort før validering (stripEmptyRows),
                // så en etterlatt tom rad ikke gir "fyll inn"-feil eller blokkerer.
                // Med forhåndsdefinerte rader er tabellen aldri "tom" — de oppfyller
                // minst-én-rad-kravet (totalsum håndheves av dependField lenger ned).
                const hasPrefilledRows = !!(element as ExpenseTableElementType).prefilledRows?.length;
                // Krev minst én rad KUN når feltet er påkrevd. Da blokkerer ikke
                // en valgfri tabell (eller en tabell der bare en tom rad ligger
                // igjen) innsending fra oppsummeringen.
                fieldSchema = hasPrefilledRows
                    ? z.preprocess(stripEmptyRows, z.array(rowSchema))
                    : isRequiredForBaseSchema(element)
                        ? z.preprocess(stripEmptyRows, z.array(rowSchema).min(1, 'Du må legge til minst én rad'))
                        : z.preprocess(stripEmptyRows, z.array(rowSchema));
            } else if (element.type === 'repeater') {
                const arr = z.array(z.any());
                if (isRequiredForBaseSchema(element)) {
                    fieldSchema = arr.min(1, 'Du må legge til minst én rad');
                } else {
                    fieldSchema = arr.optional();
                }
            } else {
                fieldSchema = z.string();

                // Check if the field is required
                if (isRequiredForBaseSchema(element)) {
                    fieldSchema = fieldSchema.nonempty(`Dette feltet er påkrevd`);

                    // Check for minimum length
                    if (element.rules?.minLength) {
                        fieldSchema = fieldSchema.min(element.rules.minLength.errorvalue, element.rules.minLength.errormessage);
                    }

                    // Check for maximum length
                    if (element.maxLength) {
                        fieldSchema = fieldSchema.max(element.maxLength, `${element.label} må inneholde mindre enn ${element.maxLength} tegn`);
                    }

                    // Check for max value
                    const maxValueRule = element.rules?.maxValue;
                    if (maxValueRule) {
                        fieldSchema = fieldSchema.refine((val) => Number(val) < maxValueRule.value, maxValueRule.errormessage);
                    }

                    // Check for min value
                    const minValueRule = element.rules?.minValue;
                    if (minValueRule) {
                        fieldSchema = fieldSchema.refine((val) => Number(val) > minValueRule.value, minValueRule.errormessage);
                    }
                } else {
                    fieldSchema = fieldSchema.optional();
                }
            }

            // Assign the field schema to the field name
            acc[element.name] = fieldSchema;

            return acc;
        }, {});

        // Felter som expensetable/budgetlines refererer for totalsum-validering
        // (dependField + forhåndsdefinert-rad-referanser) kan ligge i et ANNET
        // skjema i samme seksjon. Per-form z.object stripper ukjente nøkler, så
        // superRefine-ene under ville sett `undefined` (→ 0) for slike kryss-
        // referanser og hoppet over/feilvurdert valideringen. Ta dem med som
        // gjennomslippsfelt (z.any) så de overlever parsing. Påvirker IKKE
        // restrictedVisibility (kun disse spesifikke referansene tas med).
        const externalTotalRefs = new Set<string>();
        form.elements.forEach((el) => {
            if (el.type === 'budgetlines' || el.type === 'expensetable') {
                const dep = (el as any).dependField;
                if (dep) externalTotalRefs.add(dep);
                ((el as any).prefilledRows ?? []).forEach((p: any) => {
                    if (p?.field) externalTotalRefs.add(p.field);
                });
            }
        });
        externalTotalRefs.forEach((ref) => {
            if (!(ref in schema)) schema[ref] = z.any().optional();
        });

        let formSchema: any = z.object(schema);

        // -------------------------------------------------------------------
        // Begrenset visning (restrictedVisibility): re-aktiver required-regelen
        // for elementer som har kriterier — men KUN når kriteriene matcher
        // dagens formData (altså når feltet faktisk vises for søker).
        //
        // I basis-skjemaet over har vi gjort restricted+required-felter
        // valgfrie. Her løfter vi tilbake nødvendige feilmeldinger basert på
        // current data. Hvis feltet er skjult skjer ingenting → kommer
        // forbi validering → kan navigere videre / fullføre / Progress 100%.
        // -------------------------------------------------------------------
        // Exclude elements that have `require`-action criteria — those are handled
        // by conditionRequiredElements below, which understands the direction-aware
        // semantics. Including them here would always-enforce required regardless
        // of whether the condition currently matches.
        const restrictedRequiredFields = form.elements.filter(
            (el) =>
                isRestricted(el) &&
                isFieldRequired(el) &&
                !el.restrictedVisibility?.criteria?.some((c) => c.action === 'require')
        );
        if (restrictedRequiredFields.length > 0) {
            formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                for (const element of restrictedRequiredFields) {
                    const visible = isElementVisible(
                        (element as any).restrictedVisibility,
                        data,
                        context
                    );
                    if (!visible) continue;

                    const value = data[element.name];
                    let missing = false;

                    if (element.type === 'fileuploadsingle') {
                        missing = !value;
                    } else if (element.type === 'fileupload') {
                        missing = !Array.isArray(value) || value.length === 0;
                    } else if (element.type === 'ToggleSwitch' || element.type === 'checkbox') {
                        missing = value !== true && value !== 'true';
                    } else if (element.type === 'dropdowncheck') {
                        missing = !Array.isArray(value) || value.length === 0;
                    } else {
                        missing = value === undefined || value === null || value === '';
                    }

                    if (missing) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Dette feltet er påkrevd',
                            path: [element.name],
                        });
                    }
                }
            });
        }

        // Condition-based required/optional (require/optional actions in restrictedVisibility)
        const conditionRequiredElements = form.elements.filter(
            (el) => el.restrictedVisibility?.criteria?.some((c) => c.action === 'require' || c.action === 'optional')
        );
        if (conditionRequiredElements.length > 0) {
            formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                for (const el of conditionRequiredElements) {
                    if (!isElementVisible(el.restrictedVisibility, data, context)) continue;
                    // Use only element.required — rules.required is legacy and can be
                    // set even when element.required=false, causing false positives.
                    const baseRequired = !!el.required;
                    const override = getElementRequiredOverride(el.restrictedVisibility, data, baseRequired, context);
                    if (override !== true) continue;
                    const value = data[el.name];
                    // Av/på-felter lagres som "true"/"false"-streng, så en tom-sjekk
                    // alene godtar en avhuket boks. Krev eksplisitt påskrudd verdi.
                    const missing = (el.type === 'ToggleSwitch' || el.type === 'checkbox')
                        ? (value !== true && value !== 'true')
                        : (value === undefined || value === null || value === '');
                    if (missing) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Dette feltet er påkrevd',
                            path: [el.name],
                        });
                    }
                }
            });
        }

        // Percent-max validation (percent_max action in restrictedVisibility)
        const percentMaxElements = form.elements.filter(
            (el) => el.restrictedVisibility?.criteria?.some((c) => c.action === 'percent_max')
        );
        if (percentMaxElements.length > 0) {
            formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                for (const el of percentMaxElements) {
                    const criteria = (el.restrictedVisibility?.criteria ?? []).filter((c) => c.action === 'percent_max');
                    for (const c of criteria) {
                        if (!c.field || !c.percent) continue;
                        const elVal = Number(String(data[el.name] ?? '').replace(/\s/g, '').replace(',', '.'));
                        const refVal = Number(String(data[c.field] ?? '').replace(/\s/g, '').replace(',', '.'));
                        if (Number.isFinite(elVal) && Number.isFinite(refVal) && refVal > 0) {
                            if (elVal > refVal * (c.percent / 100)) {
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    message: c.errormessage || `Kan ikke overstige ${c.percent}% av referansefeltet`,
                                    path: [el.name],
                                });
                            }
                        }
                    }
                }
            });
        }

        if (hasRadioGroup) {
            // First, initialize inner elements in the schema
            form.elements
                .filter((el) => el.type === 'radiogroupdepending')
                .forEach((radioElement) => {
                    radioElement.depends.forEach((dep) => {
                        dep.innerElements.forEach((innerElement) => {
                            if (!schema[innerElement.name]) {
                                // Initialize as optional string with default empty value
                                schema[innerElement.name] = z.string().optional().default('');
                            }
                        });
                    });
                });

            // Apply conditional validation using superRefine
            formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                form.elements
                    .filter((el) => el.type === 'radiogroupdepending')
                    .forEach((radioElement) => {
                        const selectedValue = data[radioElement.name];

                        const activeDepend = radioElement.depends.find((d: DependType) => d.radiovalue === selectedValue);
                        if (!activeDepend) return;

                        activeDepend.innerElements.forEach((innerElement) => {
                            const isRequired = innerElement.required || innerElement.rules?.required;
                            if (!isRequired) return;

                            // Hopp over hvis "Begrenset visning" skjuler dette
                            // oppfølgings­spørsmålet basert på dagens formData.
                            if (!isElementVisible((innerElement as any).restrictedVisibility, data, context)) {
                                return;
                            }

                            const value = data[innerElement.name];
                            if (!value || value === '') {
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    message: `${innerElement.label || innerElement.name} er påkrevd`,
                                    path: [innerElement.name],
                                });
                            }

                            // Optional: Apply minLength/maxLength rules
                            if (innerElement.rules?.minLength?.errorvalue && value) {
                                if (value.length < innerElement.rules.minLength.errorvalue) {
                                    ctx.addIssue({
                                        code: z.ZodIssueCode.custom,
                                        message: innerElement.rules.minLength.errormessage,
                                        path: [innerElement.name],
                                    });
                                }
                            }

                            if (innerElement.maxLength && value) {
                                if (value.length > innerElement.maxLength) {
                                    ctx.addIssue({
                                        code: z.ZodIssueCode.custom,
                                        message: `${innerElement.label || innerElement.name} må være maks ${innerElement.maxLength} tegn`,
                                        path: [innerElement.name],
                                    });
                                }
                            }
                        });
                    });
            });
        }

        if (hasdependFieldPercent) {
            form.elements
                .filter((element) => element.rules?.dependFieldPercent)
                .forEach((element) => {
                    const dependField = element.rules?.dependFieldPercent;
                    if (!dependField || !dependField.name) {
                        console.warn('Missing dependField or dependField.name');
                        return;
                    }

                    formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                        const dependValue = data[dependField.name];

                        // Always validate, even if dependValue is missing
                        if (dependValue === undefined || dependValue === null) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Feltet ${dependField.name} mangler i data`,
                                path: [dependField.name],
                            });
                            return; // Continue checking other errors
                        }

                        switch (dependField.type) {
                            case 'greater': {
                                const maxValue = Number(dependValue?.replace(/\s+/g, '')) || 0;
                                const inputValue = Number(data[element.name]?.replace(/\s+/g, '')) || 0;

                                if (inputValue > maxValue * (dependField.percent / 100)) {
                                    ctx.addIssue({
                                        code: z.ZodIssueCode.custom,
                                        message: dependField.errormessage ?? 'DependFieldPercent error',
                                        path: [element.name],
                                    });
                                }
                                break;
                            }
                            default:
                                break;
                        }
                    });
                });
        }

        const budgetLinesWithDepend = form.elements.filter(
            (el) => el.type === 'budgetlines' && !!(el as BudgetLinesElementType).dependField
        );
        if (budgetLinesWithDepend.length > 0) {
            formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                budgetLinesWithDepend.forEach((el) => {
                    const budgetEl = el as BudgetLinesElementType;
                    const rows: any[] = Array.isArray(data[el.name]) ? data[el.name] : [];
                    const total = rows.reduce((sum: number, row: any) => {
                        return sum + (Number(String(row.belop ?? '').replace(/\s+/g, '')) || 0);
                    }, 0);
                    const depRaw = String(data[budgetEl.dependField!] ?? '').replace(/\s+/g, '');
                    const depVal = Number(depRaw) || 0;
                    if (depVal > 0 && total !== depVal) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Summen i budsjettet stemmer ikke med totalkostnaden du har oppgitt. Du må sørge for at dette stemmer før du kan fortsette!',
                            path: [el.name],
                        });
                    }
                });
            });
        }

        // Samme dependField-validering for expensetable — totalsummen (av valgt
        // totalColumn, ev. første number-kolonne) må matche dependField-verdien.
        // Lagt i selve skjemaet slik at safeParse blokkerer "Neste"/"Send inn",
        // ikke bare setter en manuell feil i errors-staten.
        const expenseTablesWithDepend = form.elements.filter(
            (el) => el.type === 'expensetable' && !!(el as ExpenseTableElementType).dependField
        );
        if (expenseTablesWithDepend.length > 0) {
            formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                expenseTablesWithDepend.forEach((el) => {
                    const expenseEl = el as ExpenseTableElementType;
                    const cols = (expenseEl.columns && expenseEl.columns.length > 0)
                        ? expenseEl.columns
                        : [{ name: 'belop', label: '', type: 'number' as const }];
                    const totalCol =
                        expenseEl.totalColumn && cols.find((c) => c.name === expenseEl.totalColumn && c.type === 'number')
                            ? expenseEl.totalColumn
                            : cols.find((c) => c.type === 'number')?.name;
                    if (!totalCol) return;

                    const rows: any[] = Array.isArray(data[el.name]) ? data[el.name] : [];
                    // Forhåndsdefinerte rader teller med i totalsummen (verdi fra formData[field]).
                    const prefilledTotal = (expenseEl.prefilledRows ?? []).reduce((sum: number, p) => {
                        return sum + (Number(String(data[p.field] ?? '').replace(/\s+/g, '')) || 0);
                    }, 0);
                    const total = prefilledTotal + rows.reduce((sum: number, row: any) => {
                        return sum + (Number(String(row[totalCol] ?? '').replace(/\s+/g, '')) || 0);
                    }, 0);
                    const depRaw = String(data[expenseEl.dependField!] ?? '').replace(/\s+/g, '');
                    const depVal = Number(depRaw) || 0;
                    if (depVal > 0 && total !== depVal) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Summen i tabellen stemmer ikke med totalkostnaden du har oppgitt (NOK ${formatNumber(depVal)}). Du må sørge for at dette stemmer før du kan fortsette!!|`,
                            path: [el.name],
                        });
                    }
                });
            });
        }

        // RadioGroup/bigradio: et valgt alternativ med "Feil (rød)"-melding
        // (description.type === 'danger') gjør feltet ugyldig, så søker ikke
        // kan gå videre. Hopper over skjulte felt (restrictedVisibility).
        const radioWithMessages = form.elements.filter(
            (el) =>
                (el.type === 'RadioGroup' || el.type === 'bigradio') &&
                Array.isArray((el as any).alternatives)
        );
        if (radioWithMessages.length > 0) {
            formSchema = formSchema.superRefine((data: FormDataType, ctx: z.RefinementCtx) => {
                for (const el of radioWithMessages) {
                    if (!isElementVisible((el as any).restrictedVisibility, data, context)) continue;
                    const alts = ((el as any).alternatives ?? []) as OptionType[];
                    const selected = alts.find((a) => String(a.value) === String(data[el.name]));
                    if (selected?.description?.type === 'danger') {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Dette alternativet kan ikke velges. Du må endre valget for å gå videre.',
                            path: [el.name],
                        });
                    }
                }
            });
        }

        if (hasAccountField) {
            // Parentesene sto feil her: `refine(pred)), { message }` gjorde
            // options-objektet til et frittstående uttrykk etter komma-operatoren,
            // så refine fikk BARE predikatet. Uten `message` og `path` ble
            // blokkeringen en rot-issue med teksten «Invalid input», som ingen
            // felt-komponent rendrer — søker fikk en død «Neste»-knapp uten spor
            // av hvorfor. `path` legger feilen på avkryssingsboksen, som er der
            // søker faktisk kan gjøre noe med den.
            formSchema = formSchema.refine(
                (data: FormDataType) =>
                    data.valid_account === 'true' ||
                    (data.valid_account === 'false' && data.confirm_invalid === 'true'),
                {
                    message: 'Du må bekrefte kontonummeret for å gå videre.',
                    path: ['confirm_invalid'],
                }
            );
        }

        if (hasImageField) {
            formSchema = formSchema.refine(
                (data: FormDataType) => {
                    // If no images are uploaded, bilder_accept is NOT required
                    if (!data.filer_bilder_multiple || data.filer_bilder_multiple.length === 0) {
                        return true;
                    }

                    // If images are uploaded, bilder_accept must be 'true'
                    return data.bilder_accept === true;
                },
                {
                    message: 'Bilder error',
                }
            );
        }

        if (hasVideoField) {
            formSchema = formSchema.refine(
                (data: FormDataType) => {
                    // If no videos are uploaded, video_accept is NOT required
                    if (!data.filer_video_multiple || data.filer_video_multiple.length === 0) {
                        return true;
                    }

                    // If videos are uploaded, video_accept must be 'true'
                    return data.video_accept === true;
                },
                {
                    message: 'Videoer error',
                }
            );
        }

        return formSchema;
    };

    const generateSectionSchema = (section: SectionType) => {
        if (!section.forms || section.forms.length === 0) {
            return null;
        }

        if (section.forms) {
            const schema = section.forms.reduce<Record<string, z.ZodObject<any>>>((acc, form) => {
                const formSchema = generateFormSchema(form);

                // Add form schema to the section
                acc[form.url] = formSchema;

                return acc;
            }, {});

            // Return the combined Zod object schema for the section
            return z.object(schema);
        }
    };

    const fullSchema = data?.sections?.reduce<Record<string, z.ZodObject<any>>>((acc, section) => {
        const sectionSchema = generateSectionSchema(section);

        if (sectionSchema) {
            acc[section.url] = sectionSchema;
        }

        return acc;
    }, {});

    // Return the full schema for the application
    return z.object(fullSchema || {});
};

export const getUserSchema = (t: (key: string) => string) => {
    return z.object({
        email: z.string().email({ message: t('input_email_error') }),
        phone: z.string().length(8, { message: t('input_phone_error') }),
        address: z.string().min(3, { message: t('input_address_error') }),
        zip: z.string().length(4, { message: t('input_zip_error') }),
        city: z.string(),
    });
};

export const flattenSchema = (schema: any) => {
    let flatSchema: any = {};
    Object.entries(schema?.shape || {}).forEach(([key, value]: [string, any]) => {
        if (value.shape) {
            // If the value is a nested schema, recursively flatten it
            flatSchema = { ...flatSchema, ...flattenSchema(value) };
        } else if (value._def?.schema?.shape) {
            flatSchema = { ...flatSchema, ...flattenSchema(value._def.schema) };
        } else if (value._def?.schema?._def?.schema?._def?.shape) {
            flatSchema = { ...flatSchema, ...flattenSchema(value._def.schema._def.schema) };
        } else {
            // Otherwise, add it directly
            flatSchema[key] = value;
        }
    });
    return flatSchema;
};

export default generateValidationSchema;
